import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, Sparkles } from 'lucide-react';
import { useClickSound } from '../../hooks/useClickSound';
const LS_KEY_INDEX = 'aura_profile_question_index';
const LS_KEY_ANSWERS = 'aura_profile_answers';
export interface ProfilingQuestionData {
  id: number;
  phase: string;
  question: string;
  type: 'text' | 'choice' | 'slider';
  options?: string[];
  sliderMin?: number;
  sliderMax?: number;
  profileKey: string;
}
export const PROFILING_QUESTIONS: ProfilingQuestionData[] = [{
  id: 1,
  phase: 'Fundamenty',
  question: 'Jak masz na imię?',
  type: 'text',
  profileKey: 'name'
}, {
  id: 2,
  phase: 'Fundamenty',
  question: 'Z czym ostatnio najtrudniej Ci sobie poradzić?',
  type: 'choice',
  options: ['Ciągłe zmęczenie', 'Gonitwa myśli', 'Problemy ze snem'],
  profileKey: 'main_struggle'
}, {
  id: 3,
  phase: 'Fundamenty',
  question: 'O jakiej porze dnia najczęściej brakuje Ci sił?',
  type: 'choice',
  options: ['Rano', 'W południe', 'Wieczorem'],
  profileKey: 'low_energy_time'
}, {
  id: 4,
  phase: 'Fundamenty',
  question: 'Gdzie w ciele najsilniej odczuwasz napięcie nerwowe?',
  type: 'choice',
  options: ['Kark i barki', 'Ścisk w brzuchu', 'Płytki oddech'],
  profileKey: 'tension_zone'
}, {
  id: 5,
  phase: 'Fundamenty',
  question: 'Przy jakich dźwiękach Twój umysł odpoczywa najlepiej?',
  type: 'choice',
  options: ['Szum natury', 'Głębokie częstotliwości', 'Absolutna cisza'],
  profileKey: 'sound_preference'
}, {
  id: 6,
  phase: 'Fundamenty',
  question: 'Próbowałaś kiedyś medytacji lub pracy z oddechem?',
  type: 'choice',
  options: ['To dla mnie nowość', 'Znam podstawy', 'Mam doświadczenie'],
  profileKey: 'meditation_experience'
}, {
  id: 7,
  phase: 'Nawyki',
  question: 'Ile kaw (kofeiny) wypijasz w ciągu dnia?',
  type: 'choice',
  options: ['0', '1-2', '3+'],
  profileKey: 'caffeine_intake'
}, {
  id: 8,
  phase: 'Nawyki',
  question: 'Jak wygląda Twój naturalny rytm dnia?',
  type: 'choice',
  options: ['Wstaję skoro świt', 'Siedzę do późna w nocy'],
  profileKey: 'chronotype'
}, {
  id: 9,
  phase: 'Nawyki',
  question: 'Ile godzin dziennie patrzysz w ekrany?',
  type: 'choice',
  options: ['< 4h', '4-8h', '8h+'],
  profileKey: 'screen_time'
}, {
  id: 10,
  phase: 'Nawyki',
  question: 'Zauważyłaś, w jaki sposób zazwyczaj oddychasz?',
  type: 'choice',
  options: ['Płytko (klatką)', 'Głęboko (brzuchem)', 'Nie wiem'],
  profileKey: 'breathing_awareness'
}, {
  id: 11,
  phase: 'Nawyki',
  question: 'Jak oceniasz jakość swojego snu z wczoraj?',
  type: 'slider',
  sliderMin: 1,
  sliderMax: 10,
  profileKey: 'sleep_quality'
}, {
  id: 12,
  phase: 'Podświadomość',
  question: 'Jaka emocja najczęściej towarzyszy Ci gdzieś w tle?',
  type: 'choice',
  options: ['Lęk i niepokój', 'Smutek', 'Frustracja i złość'],
  profileKey: 'background_emotion'
}, {
  id: 13,
  phase: 'Podświadomość',
  question: 'Jak reagujesz na nagłe, trudne sytuacje?',
  type: 'choice',
  options: ['Walczę (atakuję)', 'Wycofuję się (zamrażam)'],
  profileKey: 'stress_response'
}, {
  id: 14,
  phase: 'Podświadomość',
  question: 'Co jest Twoim największym wewnętrznym blokerem?',
  type: 'choice',
  options: ['Odkładanie na później', 'Perfekcjonizm', 'Zadowalanie innych'],
  profileKey: 'inner_blocker'
}, {
  id: 15,
  phase: 'Podświadomość',
  question: 'Z czym masz w życiu największą trudność?',
  type: 'choice',
  options: ['Odpuszczanie kontroli', 'Stawianie granic innym'],
  profileKey: 'life_difficulty'
}, {
  id: 16,
  phase: 'Upsell',
  question: 'Jakie bodźce fizyczne dają Ci najszybsze ukojenie?',
  type: 'choice',
  options: ['Ciepło i ciężar', 'Gładkość i chłód'],
  profileKey: 'physical_comfort'
}, {
  id: 17,
  phase: 'Upsell',
  question: 'Co najszybciej i najskuteczniej resetuje Twój umysł?',
  type: 'choice',
  options: ['Zapach', 'Dotyk', 'Dźwięk'],
  profileKey: 'reset_sense'
}, {
  id: 18,
  phase: 'Upsell',
  question: 'Czy w Twojej sypialni jest w nocy absolutnie ciemno?',
  type: 'choice',
  options: ['Tak', 'Nie'],
  profileKey: 'bedroom_dark'
}, {
  id: 19,
  phase: 'Upsell',
  question: 'Czego brakuje Ci w otoczeniu, by poczuć spokój?',
  type: 'choice',
  options: ['Uziemienia', 'Oczyszczenia przestrzeni'],
  profileKey: 'environment_need'
}];
// Phase colors and icons
const PHASE_STYLES: Record<string, {
  color: string;
  bg: string;
  border: string;
}> = {
  Fundamenty: {
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200'
  },
  Nawyki: {
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  },
  Podświadomość: {
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200'
  },
  Upsell: {
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200'
  }
};
export function getNextQuestionIndex(): number {
  try {
    const stored = localStorage.getItem(LS_KEY_INDEX);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
}
export function hasMoreQuestions(): boolean {
  return getNextQuestionIndex() < PROFILING_QUESTIONS.length;
}
export function getProfileAnswers(): Record<string, string> {
  try {
    const stored = localStorage.getItem(LS_KEY_ANSWERS);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}
interface ProfilingQuestionProps {
  onComplete: () => void;
}
export function ProfilingQuestion({
  onComplete
}: ProfilingQuestionProps) {
  const {
    playClick,
    playSuccess
  } = useClickSound();
  const [questionIndex] = useState(() => getNextQuestionIndex());
  const [answer, setAnswer] = useState('');
  const [sliderValue, setSliderValue] = useState(5);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const question = PROFILING_QUESTIONS[questionIndex];
  // If no more questions, auto-complete
  useEffect(() => {
    if (!question) {
      onComplete();
    }
  }, [question, onComplete]);
  if (!question) return null;
  const phaseStyle = PHASE_STYLES[question.phase] || PHASE_STYLES.Fundamenty;
  const handleSubmit = () => {
    playClick();
    // Determine the answer value
    let answerValue = '';
    if (question.type === 'text') {
      answerValue = answer.trim();
      if (!answerValue) return;
    } else if (question.type === 'choice') {
      if (!selectedOption) return;
      answerValue = selectedOption;
    } else if (question.type === 'slider') {
      answerValue = sliderValue.toString();
    }
    // Save answer
    const answers = getProfileAnswers();
    answers[question.profileKey] = answerValue;
    localStorage.setItem(LS_KEY_ANSWERS, JSON.stringify(answers));
    // Advance index
    localStorage.setItem(LS_KEY_INDEX, (questionIndex + 1).toString());
    setIsSubmitted(true);
    playSuccess();
    // Brief pause to show confirmation, then complete
    setTimeout(() => {
      onComplete();
    }, 1200);
  };
  const canSubmit = question.type === 'slider' || question.type === 'text' && answer.trim().length > 0 || question.type === 'choice' && selectedOption !== null;
  return <motion.div className="fixed inset-0 z-50 flex flex-col items-center justify-center w-full h-full bg-zen-bg text-zen-text p-6" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} data-id="element-1041">
      {/* Submitted confirmation */}
      {isSubmitted ? <motion.div className="flex flex-col items-center" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} data-id="element-1042">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-4 border-4 border-emerald-100 shadow-lg" data-id="element-1043">
            <Sparkles className="w-8 h-8 text-emerald-500" fill="currentColor" data-id="element-1044" />
          </div>
          <p className="text-lg font-medium text-zen-text" data-id="element-1045">Dziękuję ✨</p>
          <p className="text-sm text-slate-400 mt-1" data-id="element-1046">
            Twoja odpowiedź została zapisana
          </p>
        </motion.div> : <>
          {/* Phase badge */}
          <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-6" data-id="element-1047">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${phaseStyle.bg} ${phaseStyle.color} ${phaseStyle.border} border`} data-id="element-1048">
              {question.phase}
            </span>
          </motion.div>

          {/* Question icon */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15
      }} className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-1049">
            <MessageCircle className="w-7 h-7 text-slate-400" data-id="element-1050" />
          </motion.div>

          {/* Question text */}
          <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="text-2xl font-light text-center mb-8 max-w-sm leading-relaxed" data-id="element-1051">
            {question.question}
          </motion.h2>

          {/* Answer input */}
          <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="w-full max-w-sm" data-id="element-1052">
            {question.type === 'text' && <input type="text" value={answer} onChange={e => setAnswer(e.target.value)} onKeyDown={e => e.key === 'Enter' && canSubmit && handleSubmit()} placeholder="Wpisz tutaj..." autoFocus className="w-full bg-white border-2 border-slate-200 rounded-2xl px-5 py-4 text-center text-lg font-medium text-zen-text placeholder:text-slate-300 focus:outline-none focus:border-zen-sage focus:ring-2 focus:ring-zen-sage/20 transition-all shadow-sm" data-id="element-1053" />}

            {question.type === 'choice' && <div className="flex flex-col space-y-2.5" data-id="element-1054">
                {question.options?.map(option => <button key={option} onClick={() => {
            playClick();
            setSelectedOption(option);
          }} className={`w-full px-5 py-3.5 rounded-2xl text-sm font-medium transition-all border-2 text-left ${selectedOption === option ? 'bg-zen-sage/10 border-zen-sage text-zen-sage shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-1055">
                    <span className="flex items-center space-x-3" data-id="element-1056">
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${selectedOption === option ? 'border-zen-sage bg-zen-sage' : 'border-slate-300'}`} data-id="element-1057">
                        {selectedOption === option && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="w-2 h-2 bg-white rounded-full" data-id="element-1058" />}
                      </span>
                      <span data-id="element-1059">{option}</span>
                    </span>
                  </button>)}
              </div>}

            {question.type === 'slider' && <div className="flex flex-col items-center space-y-4" data-id="element-1060">
                <div className="text-5xl font-light text-zen-sage tabular-nums" data-id="element-1061">
                  {sliderValue}
                </div>
                <input type="range" min={question.sliderMin || 1} max={question.sliderMax || 10} value={sliderValue} onChange={e => setSliderValue(parseInt(e.target.value, 10))} className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-zen-sage" data-id="element-1062" />
                <div className="flex justify-between w-full text-xs text-slate-400" data-id="element-1063">
                  <span data-id="element-1064">Fatalnie</span>
                  <span data-id="element-1065">Doskonale</span>
                </div>
              </div>}
          </motion.div>

          {/* Submit button */}
          <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="w-full max-w-sm mt-8" data-id="element-1066">
            <button onClick={handleSubmit} disabled={!canSubmit} className={`w-full rounded-full py-4 text-sm font-bold transition-all flex items-center justify-center space-x-2 shadow-lg ${canSubmit ? 'bg-zen-accent text-white hover:bg-zen-accent/90 shadow-zen-accent/20' : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'}`} data-id="element-1067">
              <span data-id="element-1068">Zapisz odpowiedź</span>
              <ArrowRight className="w-4 h-4" data-id="element-1069" />
            </button>
          </motion.div>

          {/* Skip option */}
          <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} onClick={() => {
        playClick();
        // Advance index without saving
        localStorage.setItem(LS_KEY_INDEX, (questionIndex + 1).toString());
        onComplete();
      }} className="mt-4 text-xs text-slate-400 hover:text-slate-500 transition-colors" data-id="element-1070">
            Pomiń pytanie
          </motion.button>
        </>}
    </motion.div>;
}